import { useParticipantDetails } from "apps/myExperience/context/participantDetails";
import Icon from "assets/icons";
import React from "react";
import { DropdownItemConfig } from "shared/components/userDropdown/types";
import { updateDashboardDataAfterHideParticipant, useHideParticipantFromList } from "./dataloader";
import { successToast } from "shared/utils/toastNotification";
import { useCopies } from "shared/hooks/useCopies";
import { useAuth } from "shared/context/auth";
import { useQueryClient } from "react-query";
import ToggleButton from "shared/form/core/toggleButton";
import { useCommonFeatures } from "context/commonFeatures";
import Variant from "shared/enums/Variant";
import Size from "shared/enums/Size";
import { useMyExpAnalytics } from "apps/myExperience/context/analyticsWrapper";
import { AnalyticsEventNameEnum, AnalyticsScreenNameEnum } from "shared/enums/AnalyticsEnums";
import AnalyticsDatastoreType from "shared/enums/AnalyticsDatastoreType";

export default function useMyExpUserDropdownService() {
  const copies = useCopies();
  const { logEvent } = useMyExpAnalytics();
  const { logout } = useAuth();
  const queryClient = useQueryClient();
  const { participantDetails } = useParticipantDetails();
  const { initConfirmationAlert } = useCommonFeatures();

  const { mutateAsync: hideParticipant } = useHideParticipantFromList({
    registrationParticipantId: participantDetails?.registrationParticipantId,
    uiVersion: participantDetails?.uiVersion
  });

  const hideParticipantHandler = React.useCallback(() => {
    initConfirmationAlert({
      message: copies.get("make.profile.private.action.alert.text"),
      primaryButtonVariant: Variant.SUCCESS,
      handleConfirm: async () => {
        try {
          const response = await hideParticipant({});
          successToast(copies.get("changes.saved"));
          updateDashboardDataAfterHideParticipant({ queryClient, response });
          logEvent(
            AnalyticsEventNameEnum.CLICK,
            {
              screenType: AnalyticsScreenNameEnum.HIDE_PARTICIPANT_DETAILS,
              contextEntityName: String(response.hideFromAttendeeListing)
            },
            AnalyticsDatastoreType.MIXPANEL_BUSINESS
          );
        } catch (err) {}
        return true;
      }
    });
  }, [copies, hideParticipant, initConfirmationAlert, queryClient, logEvent]);

  const logoutHandler = React.useCallback(() => {
    logout();
  }, [logout]);

  const dropdownItemList = React.useMemo(() => {
    const list: Array<DropdownItemConfig> = [];
    if (participantDetails?.hideFromAttendeeListingActionPermitted) {
      list.push({
        icon: <Icon.VisibilityOffOutlined className="dropdown_item--privateActionIcon" />,
        label: null,
        doNotCloseOnClick: true,
        additionalInfo: copies.get("make.profile.private.action.subTitle"),
        customLabel: onClick => (
          <span className="dropdown_item--privateAction">
            <span className="dropdown_item--privateActionText">{copies.get("make.profile.private.action.title")}</span>
            <ToggleButton
              size={Size.SMALL}
              checked={participantDetails?.hideFromAttendeeListing}
              onChange={() => {
                hideParticipantHandler();
                onClick();
              }}
            />
          </span>
        )
      });
    }

    list.push({
      icon: <Icon.Logout />,
      label: copies.get("logout"),
      clickHandler: logoutHandler,
      isNegativeAction: true
    });

    return list;
  }, [
    copies,
    hideParticipantHandler,
    logoutHandler,
    participantDetails?.hideFromAttendeeListing,
    participantDetails?.hideFromAttendeeListingActionPermitted
  ]);

  return {
    profilePictureLocation: participantDetails?.profilePictureLocation,
    dropdownItemList
  };
}
